import React, { useEffect, useMemo, useState } from "react";
import { getSuperAdminAwardRanking } from "../api/applicationsService.js";

export default function SuperAdminAwardPage() {
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [currentPage, setCurrentPage] = useState(1);

    const ITEMS_PER_PAGE = 15;

    useEffect(() => {
        loadRanking();
    }, []);

    const loadRanking = async () => {
        try {
            setLoading(true);
            setError("");

            const data = await getSuperAdminAwardRanking();

            setApplications(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error(
                "Failed to load Super Admin award ranking",
                err
            );

            if (err?.response?.status === 401 ||
                err?.response?.status === 403) {
                setError(
                    "आपल्याला या पृष्ठावर प्रवेश करण्याची परवानगी नाही."
                );
            } else {
                setError(
                    "पुरस्कार श्रेणीक्रम माहिती लोड करताना त्रुटी आली."
                );
            }
        } finally {
            setLoading(false);
        }
    };

    const totalPages = Math.ceil(
        applications.length / ITEMS_PER_PAGE
    );

    const startIndex =
        (currentPage - 1) * ITEMS_PER_PAGE;

    const endIndex =
        startIndex + ITEMS_PER_PAGE;

    const paginatedApplications = applications.slice(
        startIndex,
        endIndex
    );

    /*
     * Backend always returns the reviewers.
     *
     * We use the first application's reviewers to determine
     * the reviewer columns.
     *
     * Backend currently guarantees 5 Admin reviewers.
     */
    const reviewers = useMemo(() => {
        if (
            applications.length === 0 ||
            !applications[0]?.reviewers
        ) {
            return [];
        }

        return applications[0].reviewers;
    }, [applications]);

    const goToPage = (pageNumber) => {
        if (pageNumber < 1 || pageNumber > totalPages) {
            return;
        }

        setCurrentPage(pageNumber);
    };

    const formatMarks = (value) => {
        if (value === null || value === undefined) {
            return "-";
        }

        return Number(value).toFixed(2);
    };

    const getStatus = (application) => {
        if (application.isCompleted) {
            return {
                label: "पूर्ण",
                className:
                    "bg-emerald-100 text-emerald-700 border border-emerald-200",
            };
        }

        return {
            label: "प्रलंबित",
            className:
                "bg-amber-100 text-amber-700 border border-amber-200",
        };
    };

    const getReviewerStatus = (reviewer) => {
        if (reviewer?.isCompleted) {
            return {
                label: "पूर्ण",
                className:
                    "bg-emerald-100 text-emerald-700",
            };
        }

        return {
            label: "प्रलंबित",
            className:
                "bg-amber-100 text-amber-700",
        };
    };

    /*
     * Loading
     */
    if (loading) {
        return (
            <div className="p-4 sm:p-7">
                <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
                    <div className="inline-flex items-center gap-3 text-slate-600">
                        <div className="w-5 h-5 border-2 border-slate-300 border-t-blue-900 rounded-full animate-spin" />

                        <span>
                            पुरस्कार श्रेणीक्रम लोड होत आहे...
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    /*
     * Error
     */
    if (error) {
        return (
            <div className="p-4 sm:p-7">
                <div className="bg-white border border-red-200 rounded-xl p-6">
                    <div className="text-red-600 font-semibold mb-2">
                        प्रवेश / डेटा त्रुटी
                    </div>

                    <p className="text-sm text-slate-600 mb-4">
                        {error}
                    </p>

                    <button
                        onClick={loadRanking}
                        className="px-4 py-2 bg-blue-900 text-white rounded-lg text-sm hover:bg-blue-800"
                    >
                        पुन्हा प्रयत्न करा
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 sm:p-7">
            {/* ------------------------------------------------ */}
            {/* PAGE HEADER                                      */}
            {/* ------------------------------------------------ */}

            <div className="flex justify-between items-end flex-wrap gap-3 mb-5">
                <div>
                    <h1
                        className="text-2xl font-normal mb-1"
                        style={{ fontFamily: "serif" }}
                    >
                        मुख्य प्रशासक — पुरस्कार निवड व श्रेणीक्रम
                    </h1>

                    <p className="text-sm text-slate-500">
                        सर्व प्रशासकांच्या परीक्षणाच्या आधारे अंतिम गुण,
                        पूर्णता व श्रेणीक्रम
                    </p>
                </div>

                <button
                    onClick={loadRanking}
                    className="bg-amber-400 hover:bg-amber-500 text-slate-900 font-bold text-xs px-4 py-2.5 rounded-lg"
                >
                    माहिती अद्ययावत करा
                </button>
            </div>

            {/* ------------------------------------------------ */}
            {/* SUMMARY                                          */}
            {/* ------------------------------------------------ */}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                    <div className="text-xs text-slate-500 mb-1">
                        एकूण अर्ज
                    </div>

                    <div className="text-2xl font-bold text-slate-800">
                        {applications.length}
                    </div>
                </div>

                <div className="bg-white border border-slate-200 rounded-xl p-4">
                    <div className="text-xs text-slate-500 mb-1">
                        परीक्षण पूर्ण
                    </div>

                    <div className="text-2xl font-bold text-emerald-600">
                        {
                            applications.filter(
                                (a) => a.isCompleted
                            ).length
                        }
                    </div>
                </div>

                <div className="bg-white border border-slate-200 rounded-xl p-4">
                    <div className="text-xs text-slate-500 mb-1">
                        परीक्षण प्रलंबित
                    </div>

                    <div className="text-2xl font-bold text-amber-600">
                        {
                            applications.filter(
                                (a) => !a.isCompleted
                            ).length
                        }
                    </div>
                </div>
            </div>

            {/* ------------------------------------------------ */}
            {/* TABLE                                            */}
            {/* ------------------------------------------------ */}

            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-xs border-collapse min-w-[1450px]">
                        <thead>
                            <tr
                                style={{ background: "#0e2a47" }}
                                className="text-slate-100"
                            >
                                {/* Rank */}
                                <th className="text-left px-3 py-3 font-semibold whitespace-nowrap">
                                    श्रेणी
                                </th>

                                {/* Application Number */}
                                <th className="text-left px-3 py-3 font-semibold whitespace-nowrap">
                                    अर्ज क्र.
                                </th>

                                {/* Teacher Name */}
                                <th className="text-left px-3 py-3 font-semibold whitespace-nowrap">
                                    शिक्षकाचे नाव
                                </th>

                                {/* Taluka */}
                                <th className="text-left px-3 py-3 font-semibold whitespace-nowrap">
                                    तालुका
                                </th>

                                {/* Cluster */}
                                <th className="text-left px-3 py-3 font-semibold whitespace-nowrap">
                                    केंद्र
                                </th>

                                {/* School */}
                                <th className="text-left px-3 py-3 font-semibold whitespace-nowrap">
                                    शाळेचे नाव
                                </th>

                                {/* Reviewer columns */}
                                {reviewers.map((reviewer, index) => (
                                    <th
                                        key={reviewer.userId || index}
                                        className="text-center px-3 py-3 font-semibold whitespace-nowrap"
                                    >
                                        <div>
                                            परीक्षक {index + 1}
                                        </div>

                                        <div className="font-normal text-[10px] text-slate-300 mt-0.5">
                                            {reviewer.reviewerName}
                                        </div>
                                    </th>
                                ))}

                                {/* Aggregate */}
                                <th className="text-center px-3 py-3 font-semibold whitespace-nowrap">
                                    सरासरी गुण
                                </th>

                                {/* Achieved */}
                                {/* <th className="text-center px-3 py-3 font-semibold whitespace-nowrap">
                                    मूळ प्राप्त गुण
                                </th> */}

                                {/* Completion */}
                                <th className="text-center px-3 py-3 font-semibold whitespace-nowrap">
                                    परीक्षण स्थिती
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            {paginatedApplications.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={6 + reviewers.length + 3}
                                        className="px-4 py-10 text-center text-slate-500"
                                    >
                                        कोणतेही अर्ज उपलब्ध नाहीत.
                                    </td>
                                </tr>
                            ) : (
                                paginatedApplications.map(
                                    (application, index) => {
                                        const status =
                                            getStatus(application);

                                        return (
                                            <tr
                                                key={application.applicationId}
                                                className={`border-b border-slate-100 ${index % 2 === 0
                                                        ? "bg-white"
                                                        : "bg-slate-50/50"
                                                    }`}
                                            >
                                                {/* -------------------------------- */}
                                                {/* RANK                              */}
                                                {/* -------------------------------- */}

                                                <td className="px-3 py-3 font-mono font-semibold">
                                                    {application.rank ?? "-"}
                                                </td>

                                                {/* -------------------------------- */}
                                                {/* APPLICATION NUMBER                */}
                                                {/* -------------------------------- */}

                                                <td className="px-3 py-3 font-mono font-semibold whitespace-nowrap">
                                                    {application.applicationNumber}
                                                </td>

                                                {/* -------------------------------- */}
                                                {/* TEACHER NAME                      */}
                                                {/* -------------------------------- */}

                                                <td className="px-3 py-3 font-medium whitespace-nowrap">
                                                    {application.teacherName}
                                                </td>

                                                {/* -------------------------------- */}
                                                {/* TALUKA                            */}
                                                {/* -------------------------------- */}

                                                <td className="px-3 py-3 whitespace-nowrap">
                                                    {application.taluka || "-"}
                                                </td>

                                                {/* -------------------------------- */}
                                                {/* CLUSTER                           */}
                                                {/* -------------------------------- */}

                                                <td className="px-3 py-3 whitespace-nowrap">
                                                    {application.cluster || "-"}
                                                </td>

                                                {/* -------------------------------- */}
                                                {/* SCHOOL                            */}
                                                {/* -------------------------------- */}

                                                <td className="px-3 py-3 whitespace-nowrap">
                                                    {application.schoolName || "-"}
                                                </td>

                                                {/* -------------------------------- */}
                                                {/* REVIEWERS                         */}
                                                {/* -------------------------------- */}

                                                {reviewers.map(
                                                    (reviewer, reviewerIndex) => {
                                                        const currentReviewer =
                                                            application.reviewers?.find(
                                                                (r) =>
                                                                    r.userId ===
                                                                    reviewer.userId
                                                            );

                                                        const reviewerStatus =
                                                            getReviewerStatus(
                                                                currentReviewer
                                                            );

                                                        return (
                                                            <td
                                                                key={
                                                                    reviewer.userId ||
                                                                    reviewerIndex
                                                                }
                                                                className="px-3 py-3 text-center"
                                                            >
                                                                <div className="font-mono font-semibold text-slate-800">
                                                                    {currentReviewer
                                                                        ? formatMarks(
                                                                            currentReviewer.reviewedMarks
                                                                        )
                                                                        : "0.00"}
                                                                </div>

                                                                <span
                                                                    className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${reviewerStatus.className}`}
                                                                >
                                                                    {
                                                                        reviewerStatus.label
                                                                    }
                                                                </span>
                                                            </td>
                                                        );
                                                    }
                                                )}

                                                {/* -------------------------------- */}
                                                {/* AGGREGATE MARKS                  */}
                                                {/* -------------------------------- */}

                                                <td className="px-3 py-3 text-center">
                                                    {application.aggregateMarks !==
                                                        null &&
                                                        application.aggregateMarks !==
                                                        undefined ? (
                                                        <span className="font-mono font-bold text-blue-900">
                                                            {formatMarks(
                                                                application.aggregateMarks
                                                            )}
                                                        </span>
                                                    ) : (
                                                        <span className="text-slate-400">
                                                            -
                                                        </span>
                                                    )}
                                                </td>

                                                {/* -------------------------------- */}
                                                {/* ACHIEVED MARKS                   */}
                                                {/* -------------------------------- */}

                                                {/* <td className="px-3 py-3 text-center font-mono">
                                                    {formatMarks(
                                                        application.achievedMarks
                                                    )}
                                                </td> */}

                                                {/* -------------------------------- */}
                                                {/* COMPLETION STATUS                */}
                                                {/* -------------------------------- */}

                                                <td className="px-3 py-3 text-center">
                                                    <span
                                                        className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-bold whitespace-nowrap ${status.className}`}
                                                    >
                                                        {status.label}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    }
                                )
                            )}
                        </tbody>
                    </table>
                </div>

                {/* ------------------------------------------------ */}
                {/* PAGINATION                                       */}
                {/* ------------------------------------------------ */}

                <div className="flex flex-col sm:flex-row justify-between items-center gap-3 px-4 py-3 border-t border-slate-200">
                    <div className="text-sm text-slate-600">
                        {applications.length > 0
                            ? `Showing ${startIndex + 1} - ${Math.min(
                                endIndex,
                                applications.length
                            )} of ${applications.length}`
                            : "Showing 0 of 0"}
                    </div>

                    {totalPages > 0 && (
                        <div className="flex items-center gap-2 flex-wrap justify-center">
                            <button
                                onClick={() =>
                                    goToPage(currentPage - 1)
                                }
                                disabled={currentPage === 1}
                                className="px-3 py-1.5 border border-slate-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100"
                            >
                                Previous
                            </button>

                            {Array.from(
                                { length: totalPages },
                                (_, i) => i + 1
                            ).map((pageNumber) => (
                                <button
                                    key={pageNumber}
                                    onClick={() =>
                                        goToPage(pageNumber)
                                    }
                                    className={`w-9 h-9 rounded ${currentPage === pageNumber
                                            ? "bg-blue-900 text-white"
                                            : "border border-slate-300 hover:bg-slate-100"
                                        }`}
                                >
                                    {pageNumber}
                                </button>
                            ))}

                            <button
                                onClick={() =>
                                    goToPage(currentPage + 1)
                                }
                                disabled={
                                    currentPage === totalPages
                                }
                                className="px-3 py-1.5 border border-slate-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}